import { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import * as Application from 'expo-application';
import { getMaintenanceStatus, getMinimumAppVersion } from '@/data/maintenance';
import compareVersions from '@/utils/compareVersions';
import { PATCH_VERSION } from '@/config/version';

const TIMEOUT_MS = 5000;

const withTimeout = (promise, ms = TIMEOUT_MS) =>
    Promise.race([
        promise,
        new Promise((resolve) => setTimeout(() => resolve(null), ms)),
    ]);

/**
 * Runs all startup checks (maintenance, minimum app version) in parallel on
 * launch. Returns a single `checksComplete` flag plus any blocking state that
 * should prevent the app from continuing.
 *
 * - Maintenance: if active, returns `isInMaintenance` + `maintenanceMessage`
 * - Version: if current app version is below the minimum, returns `updateRequired`
 *
 * OTA handling lives in useStartupUpdates: normal cold launches apply updates
 * before authentication; deep-link/notification launches never reload.
 * Resume checks remain download-only via checkForAppUpdates in _layout.jsx.
 *
 * All checks have a timeout fallback so a slow network doesn't block the app.
 * Skipped in __DEV__.
 */
const useStartupChecks = () => {
    const [checksComplete, setChecksComplete] = useState(false);
    const [isInMaintenance, setIsInMaintenance] = useState(false);
    const [maintenanceMessage, setMaintenanceMessage] = useState('');
    const [updateRequired, setUpdateRequired] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const runChecks = async () => {
            if (__DEV__) {
                if (!cancelled) setChecksComplete(true);
                return;
            }

            const fullAppVersion = `${Application.nativeApplicationVersion || '0'}.${PATCH_VERSION}`;

            // Run all checks in parallel
            const [maintenanceResult, minVersionResult] = await Promise.all([
                withTimeout(getMaintenanceStatus()),
                withTimeout(getMinimumAppVersion()),
            ]);

            if (cancelled) return;

            // 1. Maintenance check
            if (maintenanceResult) {
                setIsInMaintenance(maintenanceResult.isActive);
                setMaintenanceMessage(maintenanceResult.message);
            }

            // 2. Version check (native only — web updates automatically on deploy)
            if (minVersionResult && Platform.OS !== 'web') {
                const minVersion = Platform.OS === 'ios' ? minVersionResult.ios : minVersionResult.android;
                if (minVersion && compareVersions(fullAppVersion, minVersion) < 0) {
                    setUpdateRequired({ current: fullAppVersion, minimum: minVersion });
                }
            }

            if (!cancelled) setChecksComplete(true);
        };

        runChecks();

        return () => {
            cancelled = true;
        };
    }, []);

    return {
        checksComplete,
        isInMaintenance,
        maintenanceMessage,
        updateRequired,
    };
};

export default useStartupChecks;
